import React, { Suspense, useRef, useMemo, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import get from 'lodash/fp/get'
import isEmpty from 'lodash/isEmpty'
import isFunction from 'lodash/isFunction'
import classNames from 'classnames'
import { styled } from '@mui/material/styles'
import { Theme } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Checkbox from '@mui/material/Checkbox'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import CircularProgress from '@mui/material/CircularProgress'
import MoreVert from '@mui/icons-material/MoreVert'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state'
import { MIKE_COLORS } from '@mike/mike-shared-frontend/mike-shared-styles/mike-colors'
import { getSortedPageRows } from './support'
import TableBodyDragAndDropArea from './TableBodyDragAndDropArea'

type MikeTheme = Theme & {
  palette: Theme['palette'] & {
    mediumGrey?: { main: string }
  }
}

const StyledRoot = styled(Paper)({
  width: '100%',
  position: 'relative'
})

const StyledTable = styled(Table)({
  width: '100%'
})

const StyledFirstColumn = styled(TableCell)(({ theme }) => ({
  paddingLeft: theme.spacing(4)
}))

const StyledActionButton = styled(TableCell)(({ theme }) => ({
  width: 40,
  paddingRight: theme.spacing(4)
}))

const StyledFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 52,
  paddingLeft: theme.spacing(4)
}))

const DataTable = props => {
  const {
    headerColor,
    actions,
    loading = false,
    columns,
    idField,
    rowsPerPage,
    rowsPerPageOptions = [10, 25, 50],
    hasSelection,
    selectedRows = [],
    data = [],
    page,
    totalCount,
    notSortableColumns = [],
    // eslint-disable-next-line
    topOffset,
    // eslint-disable-next-line
    onChangePage,
    // eslint-disable-next-line
    onChangeRowsPerPage,
    // eslint-disable-next-line
    onColumnClick,
    // eslint-disable-next-line
    onHandleRequestSort,
    // eslint-disable-next-line
    onSelectionChange,
    hasMorePages,
    isSelected,
    processDroppedFiles,
    actionsPopupStateChange
  } = props

  // Migrated from recompose withState
  const [_order, _setOrder] = useState<string[]>(() =>
    props._order ? props._order : ['asc']
  )
  const [_orderBy, _setOrderBy] = useState<any[]>(() =>
    props._orderBy
      ? props._orderBy
      : [
          props.columns[0].comparator
            ? props.columns[0].comparator
            : props.columns[0].field
        ]
  )
  const [_rowsPerPage, _setRowsPerPage] = useState<number>(
    () => props._rowsPerPage ?? props.rowsPerPage ?? 25
  )
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_page, _setPage] = useState<number>(() =>
    props.page ? props.page : 0
  )

  // Migrated from recompose withHandlers
  const _isSelected = useCallback(
    item => {
      const itemId = getIdFieldValue(item, idField)
      return Boolean(
        find(
          selectedRows,
          rowItem => getIdFieldValue(rowItem, idField) === itemId
        )
      )
    },
    [selectedRows, idField]
  )

  const _handleRequestSort = useCallback(
    (_event, property) => {
      const order =
        _orderBy.includes(property) &&
        _order[_orderBy.indexOf(property)] === 'desc'
          ? 'asc'
          : 'desc'

      _setOrder([order])
      _setOrderBy([property])

      onHandleRequestSort && onHandleRequestSort(property, order)
    },
    [_order, _orderBy, onHandleRequestSort]
  )

  const _handleActionClick = useCallback(_event => {
    _event.stopPropagation()
  }, [])

  const _handleColumnClick = useCallback(
    (_event, item) => {
      _event.stopPropagation()
      onColumnClick && onColumnClick(item)
    },
    [onColumnClick]
  )

  const _handleClick = useCallback(
    (_event, item) => {
      const itemId = getIdFieldValue(item, idField)

      const selectedIndex = findIndex(
        selectedRows,
        rowItem => getIdFieldValue(rowItem, idField) === itemId
      )

      let newSelected = []
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedRows, item)
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedRows.slice(1))
      } else if (selectedIndex === selectedRows.length - 1) {
        newSelected = newSelected.concat(selectedRows.slice(0, -1))
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedRows.slice(0, selectedIndex),
          selectedRows.slice(selectedIndex + 1)
        )
      }

      onSelectionChange(newSelected)
    },
    [selectedRows, idField, onSelectionChange]
  )

  const _handleChangePage = useCallback(
    (_event, newPage) => {
      onChangePage
        ? onChangePage(newPage, capitalize(_orderBy), capitalize(_order[0]))
        : _setPage(newPage)
    },
    [onChangePage, _orderBy, _order]
  )

  const _handleChangeRowsPerPage = useCallback(
    event => {
      onChangeRowsPerPage &&
        onChangeRowsPerPage(
          event.target.value,
          capitalize(_orderBy),
          capitalize(_order[0])
        )
      _setRowsPerPage(event.target.value)
    },
    [onChangeRowsPerPage, _orderBy, _order]
  )

  // If totalCount is available or hasMorePages is true, pagination is done server side
  const pageRows =
    totalCount || hasMorePages
      ? data
      : getSortedPageRows({ ...props, _order, _orderBy })
  const selectedRowCount = useMemo(() => {
    if (isSelected) {
      return pageRows.reduce((count, row) => {
        return count + (isSelected(row) ? 1 : 0)
      }, 0)
    }
    return selectedRows.length
  }, [isSelected, selectedRows.length, pageRows])
  const allPageRowsSelected =
    totalCount > 0 && totalCount - selectedRowCount === 0
  const partiallyPageRowsSelected = selectedRowCount > 0 && !allPageRowsSelected

  const manyRowsSelected = !isEmpty(selectedRows) && selectedRows.length > 1
  const dataCount = totalCount > 0 ? totalCount : data ? data.length : 0

  const showSkeleton = loading && pageRows.length === 0
  const showInlineLoading = loading && pageRows.length > 0
  const showPagination = dataCount > 10 || totalCount === -1

  // Hidden columns are dropped from the head and the body, so the skeleton and
  // the drag-and-drop placeholder have to span the same number of cells.
  const visibleColumns = useMemo(
    () => columns.filter(item => !item.hide || item.hide === false),
    [columns]
  )

  const dragEnterCounter = useRef(0)

  const [dragOver, setDragOver] = React.useState(false)
  const handleDrop = React.useCallback(
    event => {
      event.stopPropagation()
      event.preventDefault()
      if (processDroppedFiles) {
        processDroppedFiles(event.dataTransfer.files)
        dragEnterCounter.current = 0
        setDragOver(false)
      }
    },
    [processDroppedFiles]
  )

  const handleDragOver = React.useCallback(event => {
    event.stopPropagation()
    event.preventDefault()
  }, [])

  const handleDragLeave = React.useCallback(() => {
    if (processDroppedFiles) {
      dragEnterCounter.current = dragEnterCounter.current - 1
      if (dragEnterCounter.current === 0) {
        setDragOver(false)
      }
    }
  }, [dragEnterCounter, processDroppedFiles])

  const handleDragEnter = React.useCallback(
    event => {
      event.stopPropagation()
      event.preventDefault()
      if (processDroppedFiles) {
        dragEnterCounter.current = dragEnterCounter.current + 1
        setDragOver(true)
      }
    },
    [dragEnterCounter, processDroppedFiles]
  )

  const emptyTableWithDragFunctionaly =
    processDroppedFiles && (dataCount === 0 || dragOver)

  const tableHeadCheckboxChange = event => {
    const newSelected =
      event.target.checked && !(selectedRowCount === rowsPerPage)
        ? getSortedPageRows({ ...props, _order, _orderBy })
        : []
    onSelectionChange(newSelected)
  }

  const skeletonHead = [
    ...Array(visibleColumns.length).keys(),
    actions ? 'actions' : null
  ].filter(head => head !== null)

  const skeletonRows = Array.from({ length: Math.max(_rowsPerPage ?? 0, 4) })

  return (
    <StyledRoot elevation={emptyTableWithDragFunctionaly ? 0 : 1}>
      <StyledTable
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        <EnhancedTableHead
          actions={actions}
          allPageRowsSelected={allPageRowsSelected}
          columns={visibleColumns}
          noBottomBorder={emptyTableWithDragFunctionaly}
          notSortableColumns={notSortableColumns}
          onRequestSort={_handleRequestSort}
          onTableHeadCheckboxChange={tableHeadCheckboxChange}
          order={_order}
          orderBy={_orderBy}
          partiallyPageRowsSelected={partiallyPageRowsSelected}
          hasSelection={hasSelection}
          topOffset={topOffset}
          headerColor={headerColor}
        />

        {showSkeleton ? (
          <TableBodySkeleton
            skeletonHead={skeletonHead}
            skeletonRows={skeletonRows}
          ></TableBodySkeleton>
        ) : processDroppedFiles && dataCount === 0 ? (
          <TableBodyDragAndDropArea
            dragOver={dragOver}
            columns={[
              'checkbox',
              ...Array(visibleColumns.length).keys(),
              actions ? 'actions' : null
            ]}
            height={'336px'}
          />
        ) : (
          <TableBody
            sx={[
              dragOver
                ? (theme: Theme) => ({
                    border: `3px solid ${theme.palette.secondary.main}`
                  })
                : {},
              showInlineLoading
                ? {
                    opacity: 0.5,
                    pointerEvents: 'none',
                    transition: 'opacity 150ms ease-in-out'
                  }
                : {}
            ]}
          >
            {pageRows.map(item => {
              const rowKey = getIdFieldValue(item, idField)
              const isItemSelected = isSelected
                ? isSelected(item)
                : _isSelected(item)

              return (
                <TableRow
                  hover
                  role={hasSelection !== false ? 'checkbox' : undefined}
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={rowKey}
                  selected={isItemSelected}
                >
                  {hasSelection !== false && (
                    <StyledFirstColumn
                      sx={
                        dragOver
                          ? (theme: Theme) => {
                              const t = theme as MikeTheme
                              return {
                                backgroundColor:
                                  t.palette.mediumGrey?.main ?? '#ccc',
                                opacity: 0.8
                              }
                            }
                          : undefined
                      }
                      padding="checkbox"
                      onClick={event => _handleClick(event, item)}
                    >
                      <Checkbox checked={isItemSelected} />
                    </StyledFirstColumn>
                  )}
                  {visibleColumns.map((col, index) => {
                    const value = getValue(col, item, data)
                    const cellKey = getCellKey(col, item)
                    const render = getRender(col)

                    return (
                      <TableCell
                        className={classNames(
                          col.className ? col.className(item) : ''
                        )}
                        sx={[
                          dragOver
                            ? (theme: Theme) => {
                                const t = theme as MikeTheme
                                return {
                                  backgroundColor:
                                    t.palette.mediumGrey?.main ?? '#ccc',
                                  opacity: 0.8
                                }
                              }
                            : {},
                          hasSelection === false && index === 0
                            ? (theme: Theme) => ({
                                paddingLeft: theme.spacing(4)
                              })
                            : {}
                        ]}
                        key={cellKey}
                        padding={getPadding(col)}
                        align={col.numeric ? 'right' : 'left'}
                        onClick={event => _handleColumnClick(event, item)}
                      >
                        {render(value, item, data)}
                      </TableCell>
                    )
                  })}
                  {actions ? (
                    <StyledActionButton
                      align="left"
                      sx={
                        dragOver
                          ? (theme: Theme) => {
                              const t = theme as MikeTheme
                              return {
                                backgroundColor:
                                  t.palette.mediumGrey?.main ?? '#ccc',
                                opacity: 0.8
                              }
                            }
                          : undefined
                      }
                      onClick={event => _handleActionClick(event)}
                    >
                      <Actions
                        actions={actions}
                        row={item}
                        disabled={manyRowsSelected}
                        actionsPopupStateChange={actionsPopupStateChange}
                      />
                    </StyledActionButton>
                  ) : null}
                </TableRow>
              )
            })}
          </TableBody>
        )}
      </StyledTable>
      {(showPagination || showInlineLoading) && (
        <StyledFooter>
          {showInlineLoading ? (
            <CircularProgress size={20} aria-label="Refreshing" />
          ) : (
            <span />
          )}
          {showPagination && (
            <TablePagination
              component="div"
              count={totalCount === -1 ? -1 : dataCount}
              rowsPerPage={_rowsPerPage ? _rowsPerPage : 10}
              page={page}
              rowsPerPageOptions={
                rowsPerPageOptions ? rowsPerPageOptions : [10, 25, 50]
              }
              onPageChange={_handleChangePage}
              onRowsPerPageChange={_handleChangeRowsPerPage}
              nextIconButtonProps={
                hasMorePages === false
                  ? {
                      className: 'Mui-disabled',
                      disabled: true
                    }
                  : {}
              }
            />
          )}
        </StyledFooter>
      )}
    </StyledRoot>
  )
}

export const columnPropType = PropTypes.exact({
  field: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  label: PropTypes.string.isRequired,
  numeric: PropTypes.bool,
  hide: PropTypes.bool,
  disablePadding: PropTypes.bool,
  render: PropTypes.func,
  comparator: PropTypes.func,
  className: PropTypes.any
})

DataTable.propTypes = {
  headerColor: PropTypes.string,
  actions: PropTypes.array,
  loading: PropTypes.bool,
  columns: PropTypes.arrayOf(columnPropType).isRequired,
  idField: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  rowsPerPage: PropTypes.number,
  isSelected: PropTypes.func,
  data: PropTypes.array,
  hasSelection: PropTypes.bool,
  selectedRows: PropTypes.array,
  totalCount: PropTypes.number,
  onChangeRowsPerPage: PropTypes.func,
  onColumnClick: PropTypes.func,
  onHandleRequestSort: PropTypes.func,
  onPageChange: PropTypes.func,
  onSelectionChange: PropTypes.func,
  page: PropTypes.number,
  notSortableColumns: PropTypes.array,
  hasMorePages: PropTypes.bool,
  processDroppedFiles: PropTypes.func,
  actionsPopupStateChange: PropTypes.func,
  _order: PropTypes.arrayOf(PropTypes.string),
  _orderBy: PropTypes.array,
  _rowsPerPage: PropTypes.number
}

const getValue = (col, item, data) =>
  isFunction(col.value)
    ? col.value(item, data)
    : get(getCellKey(col, item))(item)

const getCellKey = (col, item) =>
  isFunction(col.field) ? col.field(item) : col.field

const getRender = col => col.render || (value => value)

const EnhancedTableHead = ({
  actions,
  allPageRowsSelected,
  columns,
  noBottomBorder,
  notSortableColumns,
  onRequestSort,
  onTableHeadCheckboxChange,
  order,
  orderBy,
  partiallyPageRowsSelected,
  hasSelection,
  topOffset,
  headerColor
}) => (
  <TableHead component="thead" sx={{ zIndex: 1 }}>
    <TableRow sx={{ zIndex: 1 }}>
      {hasSelection !== false && (
        <TableCell
          component="th"
          padding="checkbox"
          sx={[
            {
              position: 'sticky',
              top: topOffset,
              zIndex: 1,
              whiteSpace: 'nowrap',
              backgroundColor: headerColor || 'transparent',
              paddingLeft: theme => theme.spacing(4)
            },
            noBottomBorder ? { borderBottom: 'none' } : {}
          ]}
        >
          <Checkbox
            indeterminate={partiallyPageRowsSelected}
            checked={allPageRowsSelected}
            onChange={onTableHeadCheckboxChange}
          />
        </TableCell>
      )}

      {columns.map((col, index) => {
        return (
          <TableCell
            sx={[
              {
                position: 'sticky',
                top: topOffset,
                zIndex: 1,
                whiteSpace: 'nowrap',
                backgroundColor: headerColor || 'transparent'
              },
              noBottomBorder ? { borderBottom: 'none' } : {},
              hasSelection === false && index === 0
                ? (theme: Theme) => ({ paddingLeft: theme.spacing(4) })
                : {}
            ]}
            key={col.field}
            align={col.numeric ? 'right' : 'left'}
            padding={getPadding(col)}
            component="th"
            sortDirection={
              orderBy.includes(col.comparator ? col.comparator : col.field)
                ? order[
                    col.comparator
                      ? orderBy.indexOf(col.comparator)
                      : orderBy.indexOf(col.field)
                  ]
                : false
            }
          >
            {!notSortableColumns.includes(col.field) ? (
              <TableSortLabel
                active={
                  col.comparator
                    ? orderBy.includes(col.comparator)
                    : orderBy.includes(col.field)
                }
                direction={order[0]}
                onClick={event =>
                  onRequestSort(
                    event,
                    col.comparator ? col.comparator : col.field
                  )
                }
              >
                {col.label}
              </TableSortLabel>
            ) : (
              col.label
            )}
          </TableCell>
        )
      })}
      {actions ? (
        <TableCell
          sx={[
            {
              position: 'sticky',
              top: topOffset,
              width: 40,
              zIndex: 1,
              backgroundColor: headerColor || 'transparent',
              paddingRight: theme => theme.spacing(4)
            },
            noBottomBorder ? { borderBottom: 'none' } : {}
          ]}
          align="center"
        />
      ) : null}
    </TableRow>
  </TableHead>
)
EnhancedTableHead.propTypes = {
  actions: PropTypes.array,
  allPageRowsSelected: PropTypes.bool.isRequired,
  columns: PropTypes.arrayOf(columnPropType).isRequired,
  noBottomBorder: PropTypes.bool,
  notSortableColumns: PropTypes.array,
  onRequestSort: PropTypes.func.isRequired,
  onTableHeadCheckboxChange: PropTypes.func.isRequired,
  order: PropTypes.arrayOf(PropTypes.string),
  orderBy: PropTypes.array,
  partiallyPageRowsSelected: PropTypes.bool.isRequired,
  hasSelection: PropTypes.bool,
  topOffset: PropTypes.number,
  headerColor: PropTypes.string
}

const TableBodySkeleton = ({ skeletonRows, skeletonHead }) => (
  <TableBody>
    {skeletonRows.map((_row, ind) => {
      return (
        <TableRow key={ind}>
          {skeletonHead.map((_head, index) => {
            return (
              <TableCell key={index} align="left">
                <Skeleton height={38} />
              </TableCell>
            )
          })}
          <TableCell align="left">
            <Skeleton height={38} />
          </TableCell>
        </TableRow>
      )
    })}
  </TableBody>
)
TableBodySkeleton.propTypes = {
  skeletonRows: PropTypes.array.isRequired,
  skeletonHead: PropTypes.array.isRequired
}

const lazyLoadIconByName = name => {
  return React.lazy(() =>
    import(`@mike/mike-shared-frontend/media/icons/${name.name}`).then(
      module => {
        return {
          default: module.ReactComponent
        }
      }
    )
  )
}

const ActionIcon = name => {
  const Icon = lazyLoadIconByName(name)
  return (
    <>
      <Suspense fallback="">
        <Icon />
      </Suspense>
    </>
  )
}

const Actions = ({ row, actions, disabled, actionsPopupStateChange }) => {
  const items = actions
    .map((a, index) => {
      const renderedValue = a.render
        ? a.render(row, a.parentCapabilities)
        : a.name
      if (!renderedValue) {
        return null
      }
      return {
        action: a,
        index,
        renderedValue,
        disabled:
          typeof a.disabled === 'function' ? !!a.disabled(row) : !!a.disabled
      }
    })
    .filter(item => item !== null)

  if (items.length === 0) {
    return null
  }

  return (
    <PopupState variant="popover" popupId="action-popup-menu">
      {popupState => {
        actionsPopupStateChange &&
          actionsPopupStateChange(popupState.isOpen, row)
        return (
          <React.Fragment>
            <IconButton
              {...bindTrigger(popupState)}
              disabled={disabled}
              size="large"
            >
              <MoreVert />
            </IconButton>
            <Menu
              {...bindMenu(popupState)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              MenuListProps={{
                style: {
                  padding: 0,
                  backgroundColor: MIKE_COLORS.XLIGHTGREY
                }
              }}
            >
              {items.map(item => (
                <MenuItem
                  key={item.index}
                  disabled={item.disabled}
                  onClick={e => {
                    e.preventDefault()
                    popupState.close()
                    item.action.callBack(row, item.action.name)
                  }}
                  disableGutters={item.action.disableGutters}
                >
                  {item.action.icon ? (
                    <ActionIcon name={item.action.icon} />
                  ) : (
                    ''
                  )}
                  {item.renderedValue}
                </MenuItem>
              ))}
            </Menu>
          </React.Fragment>
        )
      }}
    </PopupState>
  )
}
Actions.propTypes = {
  row: PropTypes.object.isRequired,
  actions: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  actionsPopupStateChange: PropTypes.func
}

const getPadding = col => (col.disablePadding ? 'none' : 'normal')

const capitalize = s => {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const getIdFieldValue = (item, idField) => {
  if (typeof idField === 'function') {
    return idField(item)
  }
  return item[idField]
}

export default DataTable
